import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, ArrowRight } from "lucide-react";

export default function JoinWorkspace() {
  const { token } = useParams();
  const { user, ready, refresh } = useAuth();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/workspace/invite/${token}`);
        setPreview(data);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    })();
  }, [token]);

  const accept = async () => {
    setLoading(true);
    try {
      await api.post("/workspace/join", { token });
      toast.success(`Joined ${preview?.workspace_name}`);
      await refresh();
      navigate("/app/team");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SiteHeader />
      <main className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="join-page">
        <Users className="w-12 h-12 mx-auto text-[#0F172A]" />
        {error ? (
          <>
            <h1 className="font-heading text-3xl font-bold tracking-tight mt-6">Invite unavailable</h1>
            <p className="text-zinc-600 mt-3 text-sm">{error}</p>
            <Link to="/" className="mt-6 inline-block font-mono text-xs underline">Back to home →</Link>
          </>
        ) : !preview ? (
          <div className="mt-6 text-zinc-500 font-mono text-sm">Loading invite…</div>
        ) : (
          <>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-6">Team invite</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight mt-2">
              {preview.invited_by} invited you<br/>to <span className="text-[#0F172A]">{preview.workspace_name}</span>
            </h1>
            <p className="text-zinc-600 mt-3 text-sm">
              Join to share expert-review credits and CoS history with your firm.
            </p>
            {!ready ? null : !user ? (
              <div className="mt-8 space-y-3">
                <p className="text-sm text-zinc-700">Sign in with <span className="font-mono">{preview.email}</span> to accept this invite.</p>
                <Link to={`/login?next=/join/${token}`}>
                  <Button className="w-full rounded-sm bg-[#0F172A] hover:bg-[#0F172A]/90 h-11" data-testid="join-signin-btn">
                    Sign in <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to={`/register?next=/join/${token}`}>
                  <Button variant="outline" className="w-full rounded-sm h-11">Create an account</Button>
                </Link>
              </div>
            ) : user.email?.toLowerCase() !== preview.email?.toLowerCase() ? (
              <div className="mt-8 border border-amber-300 bg-amber-50 rounded-sm p-4 text-sm text-amber-900">
                This invite is for <b>{preview.email}</b>, but you're signed in as <b>{user.email}</b>. Sign out and back in with the right account to accept.
              </div>
            ) : (
              <div className="mt-8">
                <Button onClick={accept} disabled={loading}
                        className="w-full rounded-sm bg-[#0F172A] hover:bg-[#0F172A]/90 h-11"
                        data-testid="join-accept-btn">
                  {loading ? "Joining…" : (<>Accept invite <ArrowRight className="w-4 h-4" /></>)}
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
